import { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, assetApi, transactionApi, goldApi, silverApi, bankSavingApi } from '../lib/api';
import type { Asset, AssetShare, AssetType, BankSavingContract, GoldPnl, GoldPnlBreakdown, SilverPnlBreakdown, Transaction, TransactionType } from '@asset-mgmt/shared';
import { useAuth } from '../context/AuthContext';
import { TransactionModal } from '../components/TransactionModal';
import { BankSavingContractModal } from '../components/BankSavingContractModal';
import { useToast } from '../context/ToastContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<AssetType, string> = {
  cash:         'Cash',
  bank_account: 'Bank Account',
  bank_saving:  'Bank Saving',
  investment:   'Investment',
  crypto:       'Crypto',
  real_estate:  'Real Estate',
  liability:    'Liability',
  gold:         'Gold',
  silver:       'Silver',
  custom:       'Custom',
};

const TYPE_COLORS: Record<AssetType, string> = {
  cash:         'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  bank_account: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  bank_saving:  'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400',
  investment:   'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
  crypto:       'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
  real_estate:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  liability:    'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  gold:         'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  silver:       'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300',
  custom:       'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const TXN_TYPE_STYLES: Record<TransactionType, { badge: string; sign: string; label: string }> = {
  income:   { badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', sign: '+', label: 'Income' },
  expense:  { badge: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',   sign: '−', label: 'Expense' },
  transfer: { badge: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400', sign: '↕', label: 'Transfer' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtAmount(v: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(v);
  } catch {
    return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
  }
}

function fmtBalance(v: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(Math.abs(v));
  } catch {
    return `${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}`;
  }
}

function groupByDate<T extends { date: string }>(txns: T[]): Array<{ date: string; items: T[] }> {
  const groups: Array<{ date: string; items: T[] }> = [];
  for (const t of txns) {
    const key = t.date.slice(0, 10);
    const last = groups[groups.length - 1];
    if (last?.date === key) last.items.push(t);
    else groups.push({ date: key, items: [t] });
  }
  return groups;
}

function fmtDateGroup(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Icons ──────────────────────────────────────────────────────────────────────

function ArrowLeftIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

// ── Stat Card ──────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-lg font-bold tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function AssetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const baseCurrency = user?.settings.baseCurrency ?? 'VND';
  const isViewer = user?.role === 'viewer';

  const [asset, setAsset] = useState<Asset | null>(null);
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Gold P&L state
  const [goldPnlData, setGoldPnlData] = useState<GoldPnlBreakdown | null>(null);
  const [goldPnlExpanded, setGoldPnlExpanded] = useState(false);

  // Silver P&L state
  const [silverPnlData, setSilverPnlData] = useState<SilverPnlBreakdown | null>(null);
  const [silverPnlExpanded, setSilverPnlExpanded] = useState(false);

  // Bank saving contracts state
  const [contracts, setContracts] = useState<BankSavingContract[]>([]);
  const [contractModal, setContractModal] = useState<
    | { initialMode: 'open' }
    | { initialMode: 'close'; initialContract: BankSavingContract }
    | { initialMode: 'withdraw'; initialContract: BankSavingContract }
    | { initialMode: 'rotate'; initialContract: BankSavingContract }
    | null
  >(null);

  // Share state
  const [shares, setShares] = useState<AssetShare[]>([]);
  const [shareForm, setShareForm] = useState({ email: '', permission: 'viewer' as 'editor' | 'viewer' });
  const [shareAdding, setShareAdding] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  // Whether the current user owns this asset (vs. it's shared with them)
  const isOwner = asset?.userId === user?.id;

  // Edit / delete state
  const [editTxn, setEditTxn] = useState<Transaction | null>(null);
  const [showModal, setShowModal] = useState(false);

  function reload() {
    if (!id) return;
    Promise.all([
      api.get<Asset[]>('/assets'),
      assetApi.listTransactions(id),
    ])
      .then(([assets, txns]) => {
        const found = assets.find((a) => a.id === id);
        if (!found) { setNotFound(true); setLoading(false); return; }
        setAsset(found);
        setAllAssets(assets);
        setTransactions(txns);
        // Load shares if owner
        if (found.userId === user?.id) {
          assetApi.getShares(id).then(setShares).catch(() => {});
        }        // Load gold P&L if this is a gold asset
        if (found.type === 'gold') {
          goldApi.getPnl(id).then(setGoldPnlData).catch(() => {});
        }
        // Load silver P&L if this is a silver asset
        if (found.type === 'silver') {
          silverApi.getPnl(id).then(setSilverPnlData).catch(() => {});
        }
        // Load bank saving contracts if this is a bank saving asset
        if (found.type === 'bank_saving') {
          bankSavingApi.listContracts(id).then(setContracts).catch(() => {});
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  // Filter state
  const [typeFilter, setTypeFilter] = useState<TransactionType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [sortDesc, setSortDesc] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleDelete(t: Transaction) {
    if (!confirm('Delete this transaction?')) return;
    try {
      await transactionApi.remove(t.id);
      toast.success('Transaction deleted.');
      reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete transaction.');
    }
  }

  async function handleAddShare(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setShareAdding(true);
    setShareError(null);
    try {
      const share = await assetApi.addShare(id, shareForm);
      setShares((prev) => [...prev, share]);
      setShareForm({ email: '', permission: 'viewer' });
      toast.success('Asset shared successfully.');
    } catch (err) {
      setShareError(err instanceof Error ? err.message : 'Failed to share');
    }
    setShareAdding(false);
  }

  async function handleRemoveShare(shareId: string) {
    if (!id || !confirm('Remove this share?')) return;
    try {
      await assetApi.removeShare(id, shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
      toast.success('Share removed.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove share.');
    }
  }

  async function handleUpdateShare(shareId: string, permission: 'editor' | 'viewer') {
    if (!id) return;
    const updated = await assetApi.updateShare(id, shareId, { permission }).catch(() => null);
    if (updated) {
      setShares((prev) => prev.map((s) => s.id === shareId ? { ...s, permission } : s));
      toast.success('Permission updated.');
    } else {
      toast.error('Failed to update permission.');
    }
  }

  function openEdit(t: Transaction) {
    setEditTxn(t);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditTxn(null);
    reload();
  }

  // ── Derived stats ────────────────────────────────────────────────────────────
  const totalIncome   = transactions.filter((t) => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalExpense  = transactions.filter((t) => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
  const totalTransfer = transactions.filter((t) => t.type === 'transfer').reduce((s, t) => s + t.amount, 0);

  // ── Filtered & sorted list ───────────────────────────────────────────────────
  const filtered = transactions
    .filter((t) => {
      if (typeFilter !== 'all' && t.type !== typeFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (
          !t.note?.toLowerCase().includes(q) &&
          !t.type.toLowerCase().includes(q) &&
          !t.amount.toString().includes(q) &&
          !t.date.includes(q) &&
          !t.currency.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const cmp = a.date.localeCompare(b.date);
      return sortDesc ? -cmp : cmp;
    });

  // ── Loading / not found ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound || !asset) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">Asset not found.</p>
        <Link to="/assets" className="mt-3 inline-block text-sm text-primary-600 hover:underline">← Back to Assets</Link>
      </div>
    );
  }

  const displayCurrency = asset.currency;
  const groupedFiltered = groupByDate(filtered);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* ── Back + Title ── */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/assets')}
          className="mt-0.5 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <ArrowLeftIcon />
          <span className="hidden sm:inline">Assets</span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{asset.name}</h1>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_COLORS[asset.type]}`}>
              {TYPE_LABELS[asset.type]}
            </span>
            {asset.isLiability && (
              <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                Liability
              </span>
            )}
            {asset.sharedBy && (
              <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                Shared by {asset.sharedBy}
                {asset.sharePermission && ` · ${asset.sharePermission}`}
              </span>
            )}
          </div>
          {asset.description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{asset.description}</p>
          )}
        </div>
      </div>

      {/* ── Asset Info Card ── */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Asset Details</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Current Balance</p>
              <p className={`mt-0.5 text-xl font-bold tabular-nums ${asset.isLiability ? 'text-red-500' : 'text-gray-900 dark:text-gray-100'}`}>
                {asset.type === 'gold'
                  ? `${asset.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })} chỉ`
                  : asset.type === 'silver'
                  ? `${asset.balance.toLocaleString(undefined, { maximumFractionDigits: 3 })} lượng`
                  : `${asset.isLiability ? '−' : ''}${fmtBalance(asset.balance, displayCurrency)}`}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Unit</p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
                {asset.type === 'gold' ? 'chỉ' : asset.type === 'silver' ? 'lượng' : asset.currency}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
              <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                <CalendarIcon />
                {fmtDate(asset.createdAt)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Last Updated</p>
              <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                <CalendarIcon />
                {fmtDate(asset.updatedAt)}
              </p>
            </div>
          </div>
          {asset.description && (
            <div className="mt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Description</p>
              <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-300">{asset.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label={asset.type === 'gold' ? 'Total Bought' : asset.type === 'silver' ? 'Total Bought' : 'Total Income'}
          value={asset.type === 'gold'
            ? `${transactions.filter(t => t.type === 'income').reduce((s, t) => s + (t.goldQuantity ?? 0), 0).toLocaleString()} chỉ`
            : asset.type === 'silver'
            ? `${transactions.filter(t => t.type === 'income').reduce((s, t) => s + (t.silverQuantity ?? 0), 0).toLocaleString()} lượng`
            : fmtAmount(totalIncome, displayCurrency)}
          color="text-emerald-600 dark:text-emerald-400"
        />
        <StatCard
          label={asset.type === 'gold' ? 'Total Sold' : asset.type === 'silver' ? 'Total Sold' : 'Total Expense'}
          value={asset.type === 'gold'
            ? `${transactions.filter(t => t.type === 'expense').reduce((s, t) => s + (t.goldQuantity ?? 0), 0).toLocaleString()} chỉ`
            : asset.type === 'silver'
            ? `${transactions.filter(t => t.type === 'expense').reduce((s, t) => s + (t.silverQuantity ?? 0), 0).toLocaleString()} lượng`
            : fmtAmount(totalExpense, displayCurrency)}
          color="text-red-500 dark:text-red-400"
        />
        <StatCard
          label="Transfers"
          value={asset.type === 'gold' || asset.type === 'silver' ? '—' : fmtAmount(totalTransfer, displayCurrency)}
          color="text-blue-600 dark:text-blue-400"
        />
        <StatCard
          label="Transactions"
          value={transactions.length.toString()}
          color="text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* ── Gold P&L Breakdown ── */}
      {asset.type === 'gold' && goldPnlData && (
        <div className="space-y-3">
          {/* Summary — always visible, click to expand/collapse merchant breakdown */}
          <button
            type="button"
            onClick={() => setGoldPnlExpanded((v) => !v)}
            className="w-full text-left rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-100 dark:bg-amber-900/40 shadow-sm overflow-hidden focus:outline-none"
          >
            <div className="px-5 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">🥇 Gold Investment</p>
                <p className="mt-0.5 text-sm font-bold text-amber-900 dark:text-amber-200">
                  {goldPnlData.summary.totalQuantity.toLocaleString()} chỉ • Cost {goldPnlData.summary.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} • Value {goldPnlData.summary.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-base font-bold tabular-nums ${
                  goldPnlData.summary.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                }`}>
                  {goldPnlData.summary.unrealizedPnl >= 0 ? '+' : ''}{goldPnlData.summary.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  &nbsp;({goldPnlData.summary.returnPercent >= 0 ? '+' : ''}{goldPnlData.summary.returnPercent.toFixed(2)}%)
                </span>
                {goldPnlData.breakdown.length > 0 && (
                  <svg className={`h-4 w-4 text-amber-600 dark:text-amber-400 transition-transform ${goldPnlExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>
            </div>
          </button>

          {/* Per-merchant/type breakdown cards — collapsed by default */}
          {goldPnlExpanded && goldPnlData.breakdown.map((item) => (
            <div key={`${item.merchantId}:${item.goldType}`} className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-amber-100 dark:border-amber-800">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                    {item.merchantName} • {item.goldType === 'bar' ? 'Vàng Miếng' : 'Nhẫn Tròn'}
                  </p>
                  <span className={`text-sm font-bold tabular-nums ${
                    item.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                  }`}>
                    {item.unrealizedPnl >= 0 ? '+' : ''}{item.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                    &nbsp;({item.returnPercent >= 0 ? '+' : ''}{item.returnPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
              <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Quantity Held</p>
                  <p className="mt-0.5 text-sm font-bold text-amber-900 dark:text-amber-200">{item.totalQuantity.toLocaleString()} chỉ</p>
                </div>
                <div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Avg. Buy Price</p>
                  <p className="mt-0.5 text-sm font-bold text-amber-900 dark:text-amber-200">
                    {item.avgBuyPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} / chỉ
                  </p>
                </div>
                <div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Current Sell Price</p>
                  <p className="mt-0.5 text-sm font-bold text-amber-900 dark:text-amber-200">
                    {item.currentSellPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} / chỉ
                  </p>
                </div>
                <div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Cost Basis</p>
                  <p className="mt-0.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
                    {item.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Market Value</p>
                  <p className="mt-0.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
                    {item.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-amber-600 dark:text-amber-500">Unrealized P&amp;L</p>
                  <p className={`mt-0.5 text-sm font-bold tabular-nums ${
                    item.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                  }`}>
                    {item.unrealizedPnl >= 0 ? '+' : ''}{item.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Silver P&L Breakdown ── */}
      {asset.type === 'silver' && silverPnlData && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setSilverPnlExpanded((v) => !v)}
            className="w-full text-left rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-900/40 shadow-sm overflow-hidden focus:outline-none"
          >
            <div className="px-5 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">🥈 Silver Investment</p>
                <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-200">
                  {silverPnlData.summary.totalQuantity.toLocaleString()} lượng • Cost {silverPnlData.summary.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} • Value {silverPnlData.summary.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-base font-bold tabular-nums ${
                  silverPnlData.summary.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                }`}>
                  {silverPnlData.summary.unrealizedPnl >= 0 ? '+' : ''}{silverPnlData.summary.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  &nbsp;({silverPnlData.summary.returnPercent >= 0 ? '+' : ''}{silverPnlData.summary.returnPercent.toFixed(2)}%)
                </span>
                {silverPnlData.breakdown.length > 0 && (
                  <svg className={`h-4 w-4 text-slate-600 dark:text-slate-400 transition-transform ${silverPnlExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>
            </div>
          </button>

          {silverPnlExpanded && silverPnlData.breakdown.map((item) => (
            <div key={`${item.merchantId}:${item.silverType}`} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/20 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                    {item.merchantName} • {item.silverType === 'bar' ? 'Bạc Miếng' : 'Bạc 1kg'}
                  </p>
                  <span className={`text-sm font-bold tabular-nums ${
                    item.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                  }`}>
                    {item.unrealizedPnl >= 0 ? '+' : ''}{item.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                    &nbsp;({item.returnPercent >= 0 ? '+' : ''}{item.returnPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
              <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Quantity Held</p>
                  <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-200">{item.totalQuantity.toLocaleString()} {item.silverType === 'bar1kg' ? 'kg' : 'lượng'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Avg. Buy Price</p>
                  <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-200">
                    {item.avgBuyPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} / {item.silverType === 'bar1kg' ? 'kg' : 'lượng'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Current Sell Price</p>
                  <p className="mt-0.5 text-sm font-bold text-slate-900 dark:text-slate-200">
                    {item.currentSellPrice.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })} / {item.silverType === 'bar1kg' ? 'kg' : 'lượng'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Cost Basis</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-300">
                    {item.totalCost.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Market Value</p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-300">
                    {item.currentValue.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-600 dark:text-slate-500">Unrealized P&amp;L</p>
                  <p className={`mt-0.5 text-sm font-bold tabular-nums ${
                    item.unrealizedPnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
                  }`}>
                    {item.unrealizedPnl >= 0 ? '+' : ''}{item.unrealizedPnl.toLocaleString('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Bank Saving Contracts ── */}
      {asset.type === 'bank_saving' && (
        <div className="rounded-xl border border-teal-200 dark:border-teal-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-teal-100 dark:border-teal-800 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-teal-800 dark:text-teal-300">
                🏦 Term Deposit Contracts
              </h2>
              <p className="text-xs text-teal-600 dark:text-teal-500 mt-0.5">
                {contracts.filter((c) => c.status !== 'closed').length} active
                {' · '}
                Est. total value:{' '}
                <strong>
                  {contracts
                    .filter((c) => c.status !== 'closed')
                    .reduce((s, c) => s + c.estimatedEndValue, 0)
                    .toLocaleString(undefined, { maximumFractionDigits: 0 })}{' '}
                  {asset.currency}
                </strong>
              </p>
            </div>
            {!isViewer && (
              <button
                onClick={() => setContractModal({ initialMode: 'open' })}
                className="flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 transition-colors"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Open Contract
              </button>
            )}
          </div>

          {contracts.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
              No contracts yet. Open one to get started.
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {contracts.map((contract) => {
                const interest  = contract.estimatedEndValue - contract.remainingPrincipal;
                const isOverdue = contract.status !== 'closed' &&
                  new Date(contract.estimatedEndDate) < new Date(new Date().toISOString().slice(0, 10));
                const statusColor = isOverdue
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  : contract.status === 'active'
                  ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                  : contract.status === 'partial'
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';

                return (
                  <div key={contract.id} className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${
                    isOverdue ? 'bg-orange-50 dark:bg-orange-900/10 border-l-4 border-l-orange-400 dark:border-l-orange-500' : ''
                  }`}>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">{contract.bankName}</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>
                          {isOverdue ? 'overdue' : contract.status}
                        </span>
                        {isOverdue && (
                          <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                            ⚠ Matured {Math.floor((new Date().getTime() - new Date(contract.estimatedEndDate).getTime()) / 86_400_000)}d ago — action needed
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Principal:{' '}
                        <strong className="text-gray-700 dark:text-gray-300">
                          {contract.remainingPrincipal !== contract.principal
                            ? `${contract.remainingPrincipal.toLocaleString()} / ${contract.principal.toLocaleString()}`
                            : contract.principal.toLocaleString()}{' '}
                          {contract.currency}
                        </strong>
                        {' · '}
                        {contract.interestRate}% p.a.
                        {' · '}
                        {contract.periodMonths}m
                        {' · '}
                        {contract.openDate} → {contract.estimatedEndDate}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Est. maturity value:{' '}
                        <strong className="text-teal-700 dark:text-teal-300">
                          {contract.estimatedEndValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} {contract.currency}
                        </strong>
                        {interest > 0 && (
                          <span className="ml-1 text-emerald-600 dark:text-emerald-400">
                            (+{interest.toLocaleString(undefined, { maximumFractionDigits: 0 })} interest)
                          </span>
                        )}
                      </div>
                    </div>

                    {!isViewer && contract.status !== 'closed' && (
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {isOverdue && (
                          <button
                            onClick={() => setContractModal({ initialMode: 'rotate', initialContract: contract })}
                            className="rounded-lg border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 px-2.5 py-1.5 text-xs font-medium text-orange-700 dark:text-orange-300 hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors"
                          >
                            Rotate
                          </button>
                        )}
                        <button
                          onClick={() => setContractModal({ initialMode: 'withdraw', initialContract: contract })}
                          className="rounded-lg border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 px-2.5 py-1.5 text-xs font-medium text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 transition-colors"
                        >
                          Withdraw
                        </button>
                        <button
                          onClick={() => setContractModal({ initialMode: 'close', initialContract: contract })}
                          className="rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-2.5 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Transaction list ── */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        {/* Header + filters */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex-1">
            Transactions
            <span className="ml-2 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
              {filtered.length}
            </span>
          </h2>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-36 sm:w-44 pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:focus:ring-primary-800"
              />
            </div>

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TransactionType | 'all')}
              className="text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-200 dark:[color-scheme:dark]"
            >
              <option value="all">All types</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
              <option value="transfer">Transfer</option>
            </select>

            {/* Sort toggle */}
            <button
              onClick={() => setSortDesc((p) => !p)}
              title={sortDesc ? 'Newest first' : 'Oldest first'}
              className="flex items-center gap-1 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 px-2.5 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              {sortDesc ? (
                <>
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" /></svg>
                  Newest
                </>
              ) : (
                <>
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" /></svg>
                  Oldest
                </>
              )}
            </button>
          </div>
        </div>

        {/* Desktop table */}
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">
            No transactions found.
          </div>
        ) : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50/80 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Note</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                    <th className="px-4 py-2.5 text-right">Currency</th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {groupedFiltered.map(({ date, items }) => (
                    <Fragment key={date}>
                      <tr className="bg-gray-50/60 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
                        <td colSpan={6} className="px-4 py-1.5">
                          <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                            {fmtDateGroup(date)}
                          </span>
                        </td>
                      </tr>
                      {items.map((t) => {
                        const style = TXN_TYPE_STYLES[t.type];
                        const isOut = t.type === 'expense' || (t.type === 'transfer' && t.assetId === id);
                        return (
                          <tr key={t.id} className="group border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50/60 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(t.date)}</td>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${style.badge}`}>
                                {style.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[220px]">
                              <div className="truncate">{t.note || <span className="text-gray-300 dark:text-gray-600">—</span>}</div>
                              {t.description && (
                                <div className="mt-0.5 truncate text-xs text-gray-400 dark:text-gray-500">{t.description}</div>
                              )}
                              {t.createdByName && (
                                <div className="mt-0.5 text-xs text-violet-500 dark:text-violet-400">by {t.createdByName}</div>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-right font-mono tabular-nums font-medium ${isOut ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                              {isOut ? '−' : '+'}{t.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-400 dark:text-gray-500 text-xs">{t.currency}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openEdit(t)}
                                  disabled={isViewer}
                                  title={isViewer ? 'Viewers cannot edit transactions' : 'Edit'}
                                  className="rounded-lg p-1.5 text-gray-400 hover:bg-primary-50 hover:text-primary-600 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                <button
                                  onClick={() => void handleDelete(t)}
                                  disabled={isViewer}
                                  title={isViewer ? 'Viewers cannot delete transactions' : 'Delete'}
                                  className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700/50">
              {groupedFiltered.map(({ date, items }) => (
                <div key={date}>
                  <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 bg-gray-50/60 dark:bg-gray-900/30">
                    {fmtDateGroup(date)}
                  </p>
                  {items.map((t) => {
                    const style = TXN_TYPE_STYLES[t.type];
                    const isOut = t.type === 'expense' || (t.type === 'transfer' && t.assetId === id);
                    return (
                      <div key={t.id} className="px-4 py-3 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${style.badge}`}>
                              {style.label}
                            </span>
                          </div>
                          {t.note && (
                            <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 truncate">{t.note}</p>
                          )}
                          {t.description && (
                            <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 truncate">{t.description}</p>
                          )}
                          {t.createdByName && (
                            <p className="mt-0.5 text-xs text-violet-500 dark:text-violet-400">by {t.createdByName}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className={`font-mono text-sm font-semibold tabular-nums ${isOut ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                            {isOut ? '−' : '+'}{t.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{t.currency}</p>
                          <div className="flex items-center justify-end gap-1 mt-1.5">
                            <button
                              onClick={() => openEdit(t)}
                              disabled={isViewer}
                              title={isViewer ? 'Viewers cannot edit transactions' : undefined}
                              className="rounded-lg px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => void handleDelete(t)}
                              disabled={isViewer}
                              title={isViewer ? 'Viewers cannot delete transactions' : undefined}
                              className="rounded-lg px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <TransactionModal
          assets={allAssets}
          initialData={editTxn}
          onClose={closeModal}
        />
      )}

      {contractModal && (
        <BankSavingContractModal
          asset={asset}
          bankAssets={allAssets.filter((a) => a.type === 'bank_account')}
          onClose={() => setContractModal(null)}
          onSuccess={() => {
            setContractModal(null);
            reload();
          }}
          {...contractModal}
        />
      )}

      {/* ── Sharing section (owner only) ── */}
      {isOwner && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700 dark:text-gray-200">Sharing</h2>

          {shares.length > 0 && (
            <ul className="mb-4 space-y-2">
              {shares.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 dark:border-gray-700 px-3 py-2 text-sm">
                  <span className="text-gray-700 dark:text-gray-300 text-xs truncate">
                    {s.sharedWithEmail ?? s.sharedWithUserId}
                    {s.sharedWithDisplayName && (
                      <span className="ml-1 text-gray-400 dark:text-gray-500">({s.sharedWithDisplayName})</span>
                    )}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={s.permission}
                      onChange={(e) => void handleUpdateShare(s.id, e.target.value as 'editor' | 'viewer')}
                      className="rounded border border-gray-200 dark:border-gray-600 bg-transparent text-xs px-1.5 py-0.5"
                    >
                      <option value="viewer">viewer</option>
                      <option value="editor">editor</option>
                    </select>
                    <button onClick={() => void handleRemoveShare(s.id)} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <form onSubmit={(e) => void handleAddShare(e)} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              placeholder="Email address"
              value={shareForm.email}
              onChange={(e) => setShareForm({ ...shareForm, email: e.target.value })}
              className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-primary-200"
            />
            <select
              value={shareForm.permission}
              onChange={(e) => setShareForm({ ...shareForm, permission: e.target.value as 'editor' | 'viewer' })}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-primary-200"
            >
              <option value="viewer">viewer</option>
              <option value="editor">editor</option>
            </select>
            <button
              type="submit"
              disabled={shareAdding}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {shareAdding ? 'Sharing…' : 'Share'}
            </button>
          </form>
          {shareError && <p className="mt-2 text-xs text-red-500">{shareError}</p>}
        </div>
      )}
    </div>
  );
}
